import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users2, Hand, X, CalendarDays, Clock3, CheckCircle2, Circle } from "lucide-react";

import SubmissionButton from "../../adminView/components/ProjectCalendar/components/submissionButton";
import SubmissionModal from "../../adminView/components/ProjectCalendar/components/SubmissionModal";
import SubmissionsForum from "../../adminView/components/SubmissionsForum/SubmissionsForum";

import TransitionModal from "../../adminView/components/ProjectCalendar/components/TransitionModal";
import FeedbackModal from "../../adminView/components/ProjectCalendar/components/Feedback/FeedbackModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Avatar = ({ name, src }) => {
  const initials = (name || "You")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const isValidSrc =
    src &&
    typeof src === "string" &&
    src.trim().length > 5 &&
    !/^https?:\/\/(?:www\.)?(via\.)?placeholder\.com/i.test(src) &&
    !/default|avatar|person\.png/i.test(src);

  return (
    <div className="relative h-10 w-10">
      <span
        className="pointer-events-none absolute -inset-1 rounded-xl
                   bg-gradient-to-tr from-fuchsia-300/30 via-sky-300/25 to-emerald-300/30
                   blur-lg animate-pulse"
      />
      {isValidSrc ? (
        <img
          src={src}
          alt={name || "Profile"}
          onError={(e) => {
            e.currentTarget.style.display = "none";
            e.currentTarget.insertAdjacentHTML(
              "afterend",
              `<div class='relative h-10 w-10 rounded-xl bg-neutral-100 text-gray-700 
                       grid place-items-center text-sm font-semibold ring-1 ring-black/5'>
                 ${initials}
               </div>`
            );
          }}
          className="relative h-10 w-10 rounded-xl object-cover ring-1 ring-black/5"
        />
      ) : (
        <div className="relative h-10 w-10 rounded-xl bg-neutral-100 text-gray-700 
                        grid place-items-center text-sm font-semibold ring-1 ring-black/5">
          {initials}
        </div>
      )}
    </div>
  );
};

const CheckpointModalTeam = ({ checkpoint, onClose, teamMembers = [], submissions = [], currentUser }) => {
  console.log("🚀 Modal opened with checkpoint:", checkpoint);
  const [selectedMember, setSelectedMember] = useState(null);
  const [hasPickedUp, setHasPickedUp] = useState(
    checkpoint.assignedTo?.email === currentUser?.email
  );

  useEffect(() => {
    const loadUid = async () => {
      try {
        if (!currentUser?.email || currentUser.uid) return; // already has uid

        const token = localStorage.getItem("token");
        const encodedEmail = encodeURIComponent(currentUser.email);

        const res = await fetch(
          `${BACKEND_URL}/user/${encodedEmail}/get-uid`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const data = await res.json();
        if (!res.ok) throw new Error(data.message);

        // inject uid dynamically
        currentUser.uid = data.uid;

        console.log("🔵 Team modal loaded UID:", data.uid);
      } catch (err) {
        console.error("❌ Failed to load UID for team modal:", err);
      }
    };

    loadUid();
  }, [currentUser]);


    // Mirror Admin logic for submission mapping
  console.log("🧩 Matching logic check (Team):", {
    clickedDate: checkpoint.clickedDate,
    checkpointId: checkpoint.checkpointId,
    allSubmissionIds: submissions?.map((s) => s.checkpointId),
  });

  const toLocalDateKey = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const da = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${da}`;
  };

  const submissionsForThisCheckpoint = submissions?.filter((s) => {
    const submittedDate = toLocalDateKey(s?.dateSubmittedFor);
    const clickedDate = toLocalDateKey(checkpoint?.clickedDate);
    return (
      s?.checkpointId === checkpoint?.checkpointId &&
      submittedDate === clickedDate
    );
  });

  console.log("🧩 Filtered submissions (Team):", {
    clickedDate: checkpoint?.clickedDate,
    allDates: submissions?.map((s) => s.dateSubmittedFor),
    matched: submissionsForThisCheckpoint?.map((s) => ({
      id: s.id,
      checkpointId: s.checkpointId,
      dateSubmittedFor: s.dateSubmittedFor,
    })),
  });

  const submissionDateDisplay =
    submissionsForThisCheckpoint?.length > 0
      ? new Date(
          submissionsForThisCheckpoint[0].dateSubmittedFor
        ).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : null;

  // --- Submission ---
  const [isSubmissionOpen, setSubmissionOpen] = useState(false);
  const [isTransitionOpen, setTransitionOpen] = useState(false);
  const [isFeedbackOpen, setFeedbackOpen] = useState(false);
  const [pendingSubmission, setPendingSubmission] = useState(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const [hoverPickup, setHoverPickup] = useState(false);
  const [hoverSubmission, setHoverSubmission] = useState(false);
  
  if (!checkpoint) return null;

  // Status calculation identical to Admin
  const toUTCKey = (date) => new Date(date).toISOString().split("T")[0];
  const nowKey = toUTCKey(new Date());
  const startKey = toUTCKey(checkpoint.startDate);
  const endKey = toUTCKey(checkpoint.endDate);
  let checkpointStatus = "";
  if (nowKey > endKey) checkpointStatus = "expired";
  else if (nowKey < startKey) checkpointStatus = "upcoming";
  else checkpointStatus = "active";

  const colorMap = {
    active: "text-green-600 bg-green-50 border-green-200",
    upcoming: "text-blue-600 bg-blue-50 border-blue-200",
    expired: "text-red-600 bg-red-50 border-red-200",
  };
  const dotMap = {
    active: "bg-green-500",
    upcoming: "bg-blue-500",
    expired: "bg-red-500",
  };
  const labelMap = {
    active: "Active",
    upcoming: "Upcoming",
    expired: "Expired",
  };

  const statusColor = colorMap[checkpointStatus];
  const dotColor = dotMap[checkpointStatus];
  const statusLabel = labelMap[checkpointStatus];
  const isExpired = checkpointStatus === "expired";

  const getDisplayName = (m) =>
    m?.firstName || m?.lastName
      ? `${m.firstName || ""} ${m.lastName || ""}`.trim()
      : m?.email?.split("@")[0] || "Unknown";

  const handlePickUp = async () => {
    if (isExpired) {
      alert("🚫 Cannot pick up expired checkpoints.");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const projectId =
        checkpoint.projectId ||
        window.location.pathname.split("/projects/")[1]?.split("/")[0];

      const body = {
        phaseName: checkpoint.phase,
        checkpointIndex: checkpoint.index,
        memberId: currentUser?.uid
      };

      // 🧪 DEEP FRONTEND DEBUG
      // console.log("🧪 [PickUp] Debug payload:", {
      //   projectId,
      //   body,
      //   checkpoint_raw: checkpoint,
      //   currentUser,
      // });

      // 🧪 Validate required pieces BEFORE calling backend
      if (!projectId) {
        console.error("❌ [PickUp] Missing projectId for checkpoint", checkpoint);
        alert("⚠️ Cannot pick up: projectId is missing for this checkpoint.");
        return;
      }

      if (!body.phaseName) {
        console.error("❌ [PickUp] Missing phaseName for checkpoint", checkpoint);
        alert("⚠️ Cannot pick up: this checkpoint has no phase name. Ask admin to re-sync the roadmap.");
        return;
      }

      if (typeof body.checkpointIndex !== "number") {
        console.error("❌ [PickUp] Invalid checkpointIndex:", body.checkpointIndex, "checkpoint:", checkpoint);
        alert("⚠️ Cannot pick up: this checkpoint is misconfigured (index missing).");
        return;
      }

      if (!currentUser?.uid) {
        console.error("❌ [PickUp] currentUser.uid missing:", currentUser);
        alert("⚠️ Cannot pick up: user is not fully initialized. Try refreshing.");
        return;
      }

      // 🧠 Check if already working (frontend)
      const alreadyWorking =
        (Array.isArray(checkpoint.pickedUpBy) &&
          checkpoint.pickedUpBy.some((m) => m.email === currentUser?.email)) ||
        checkpoint.assignedTo?.email === currentUser?.email ||
        (Array.isArray(checkpoint.assignedTo) &&
          checkpoint.assignedTo.some((m) => m.email === currentUser?.email));

      console.log("🧪 [PickUp] Already working check:", {
        pickedUpBy: checkpoint.pickedUpBy,
        assignedTo: checkpoint.assignedTo,
        currentEmail: currentUser?.email,
        alreadyWorking,
      });

      if (alreadyWorking) {
        alert("⚠️ You are already working on this.");
        return;
      }

      // 🛰️ Proceed with API call
      console.log("📡 [PickUp] Sending request to:", `${BACKEND_URL}/roadmaps/${projectId}/member/pick-up`);

      const res = await fetch(
        `${BACKEND_URL}/roadmaps/${projectId}/member/pick-up`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        }
      );

      let data;
      try {
        data = await res.json();
      } catch (parseErr) {
        console.error("❌ [PickUp] Failed to parse JSON from response.", parseErr);
        const text = await res.text();
        console.log("🧪 [PickUp] Raw response text:", text);
        throw new Error("Internal server error.");
      }

      console.log("🧪 [PickUp] Response status + body:", { status: res.status, data });

      if (!res.ok) {
        // Some known backend messages
        if (
          data?.message?.includes("already picked up") ||
          data?.message?.includes("must be assigned")
        ) {
          alert("⚠️ You are already working on this.");
          return;
        }

        if (data?.message?.includes("not a member")) {
          alert("🚫 You’re not a member of this project.");
          return;
        }

        // Anything else → surface message + log
        throw new Error(data?.message || "Internal server error.");
      }

      // ✅ Success
      alert("✅ You successfully picked up this checkpoint!");
      setHasPickedUp(true);
    } catch (error) {
      console.error("❌ Error picking up checkpoint (frontend):", error);
      alert("Something went wrong while picking up the checkpoint.");
    }
  };


  // phase 1 — user writes description
  const handleSubmitDescription = (data) => {
    setPendingSubmission({ description: data.description });
    setSubmissionOpen(false);
    setTransitionOpen(true);
  };

  // phase 2 — after transition
  const handleContinueToFeedback = () => {
    setTransitionOpen(false);
    setFeedbackOpen(true);
  };

  // phase 3 — user writes feedback → send full submission
const handleSubmitFeedback = async (feedbackData) => {
  try {
    const token = localStorage.getItem("token");
    if (!token) throw new Error("No token found");

    let roadmapId = checkpoint?.roadmapId;

    // 🧭 Mirror AdminModal logic exactly
    if (!roadmapId && checkpoint?.projectId) {
      const roadmapRes = await fetch(`${BACKEND_URL}/roadmaps/${checkpoint.projectId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const roadmapData = await roadmapRes.json();
      roadmapId = roadmapData?.roadmaps?.[0]?.id;
    }

    if (!roadmapId) throw new Error("Missing roadmapId");

    const submissionPayload = {
      description: pendingSubmission?.description,
      feedback: feedbackData,
      progressDelta: 0.1,
      hours: checkpoint.hours || 1,
      dateSubmittedFor: checkpoint.clickedDate || null,
    };

    const res = await fetch(
      `${BACKEND_URL}/submissions/${roadmapId}/${checkpoint.phase}-${checkpoint.index}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(submissionPayload),
      }
    );

    const result = await res.json();
    if (!res.ok) throw new Error(result.message || "Submission failed");

    console.log("✅ Submission + Feedback recorded (Team Modal):", result);
    setFeedbackOpen(false);
    setPendingSubmission(null);
    alert("✅ Submission + Feedback recorded successfully!");
  } catch (err) {
    console.error("❌ Error submitting full submission (Team Modal):", err);
    alert("Failed to record submission: " + err.message);
  }
};


//   const handleRequestCollab = () => {
//     if (!selectedMember) return alert("Select someone first.");
//     alert(`🤝 Collaboration request sent to ${getDisplayName(selectedMember)}! (frontend only)`);
//     setSelectedMember(null);
//   };

  const startDisplay = new Date(checkpoint.startDate).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const endDisplay = new Date(checkpoint.endDate).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
      >
        <motion.div
          initial={{ scale: 0.92, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.92, opacity: 0 }}
          transition={{ type: "spring", stiffness: 180, damping: 22 }}
          className="relative flex flex-col md:flex-row w-full max-w-4xl 
                     bg-white/80 backdrop-blur-xl border border-white/40 
                     shadow-[0_8px_30px_rgba(0,0,0,0.1)] rounded-2xl overflow-hidden"
        >
          {/* Close */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-gray-400 hover:text-black transition"
          >
            <X size={22} />
          </button>

          {/* Left Panel */}
          <div className="md:w-1/2 w-full p-8 border-r border-white/30 flex flex-col justify-center">
            <div className="flex items-center gap-3 mb-3">
              <CheckCircle2 className="text-blue-500" size={22} />
              <h2 className="text-lg font-semibold text-gray-900 tracking-tight">
                {checkpoint.phase} Phase
              </h2>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">{checkpoint.text}</p>

            {/* 🧑‍💻 Assigned + Picked-Up Avatars */}
            {(checkpoint.assignedTo?.length > 0 || checkpoint.pickedUpBy?.length > 0) && (
            <div className="flex flex-wrap gap-3 mb-4">
                {/* Assigned To Members (Blue ring) */}
                {(Array.isArray(checkpoint.assignedTo)
                  ? checkpoint.assignedTo
                  : checkpoint.assignedTo
                  ? [checkpoint.assignedTo]
                  : []
                ).map((m, i) => (

                <div
                    key={`assigned-${i}`}
                    className="relative hover:scale-105 transition-transform"
                    title={`Assigned: ${m.firstName || ""} ${m.lastName || m.email}`}
                >
                    <div className="relative">
                    <Avatar
                        name={`${m.firstName || ""} ${m.lastName || ""}`}
                        src={m.profileImage}
                    />
                    <span className="absolute -bottom-1 -right-1 h-3 w-3 bg-blue-500 border-2 border-white rounded-full" />
                    </div>
                </div>
                ))}

                {/* Picked-Up Members (Green ring) */}
                {(Array.isArray(checkpoint.pickedUpBy)
                  ? checkpoint.pickedUpBy
                  : checkpoint.pickedUpBy
                  ? [checkpoint.pickedUpBy]
                  : []
                ).map((m, i) => (

                <div
                    key={`pickup-${i}`}
                    className="relative hover:scale-105 transition-transform"
                    title={`Picked Up: ${m.firstName || ""} ${m.lastName || m.email}`}
                >
                    <div className="relative">
                    <Avatar
                        name={`${m.firstName || ""} ${m.lastName || ""}`}
                        src={m.profileImage}
                    />
                    <span className="absolute -bottom-1 -right-1 h-3 w-3 bg-emerald-500 border-2 border-white rounded-full" />
                    </div>
                </div>
                ))}
            </div>
            )}

            {/* ✅ Status */}
            <div
              className={`inline-flex items-center gap-2 px-3 py-[3px] text-xs font-medium rounded-full border ${statusColor}`}
            >
              <span className={`w-2 h-2 rounded-full ${dotColor}`} />
              {statusLabel} Checkpoint
            </div>

            <div className="grid grid-cols-2 gap-y-3 text-sm text-gray-700 mt-6">
              <div className="flex items-center gap-2 text-gray-500">
                <CalendarDays size={16} />
                <span>Start</span>
              </div>
              <span>{startDisplay}</span>
              <div className="flex items-center gap-2 text-gray-500">
                <CalendarDays size={16} />
                <span>End</span>
              </div>
              <span>{endDisplay}</span>

               <div className="flex items-center gap-2 text-gray-500">
                  <CalendarDays size={16} />
                  <span>Selected</span>
                </div>
                <span>
                  {checkpoint.clickedDate
                    ? (() => {
                        const [year, month, day] = checkpoint.clickedDate.split("-");
                        const localDate = new Date(year, month - 1, day); // Force local time
                        return localDate.toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        });
                      })()
                    : "—"}
                </span>
                              
              <div className="flex items-center gap-2 text-gray-500">
                <Clock3 size={16} />
                <span>Hours</span>
              </div>
              <span>{checkpoint.hours}</span>
              <span className="text-gray-500">Week</span>
              <span>{checkpoint.week}</span>
            </div>
          </div>

          {/* Right Panel */}
          <div className="md:w-1/2 w-full p-8 flex flex-col justify-between bg-gradient-to-br from-slate-50/60 to-white/70">
            <div>
              {/* Pick Up */}
              {/* <div className="flex items-center gap-2 mb-3">
                <Hand size={18} className="text-gray-700" />
                <p className="text-sm font-medium text-gray-700">Pick Up Checkpoint</p>
              </div> */}

              {/* <button
                onClick={handlePickUp}
                disabled={hasPickedUp || isExpired}
                className={`w-full mb-6 py-2 rounded-xl text-sm font-medium transition ${
                  hasPickedUp
                    ? "bg-green-100 text-green-700 cursor-default"
                    : isExpired
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700"
                }`}
              >
                {hasPickedUp
                  ? "✅ You’re working on this"
                  : isExpired
                  ? "Expired (Cannot Pick Up)"
                  : "Pick Up This Checkpoint"}
              </button> */}

              {/* Collaboration */}
              {/* <div className="flex items-center gap-2 mb-3">
                <Users2 size={18} className="text-gray-700" />
                <p className="text-sm font-medium text-gray-700">Request Collaboration</p>
              </div> */}

              <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-800 mb-2">Submissions</h3>
                  <div className="border border-white/30 rounded-xl p-3 bg-white/60 
                                  max-h-56 overflow-y-auto overflow-x-hidden
                                  isolate">
                    <SubmissionsForum submissions={submissionsForThisCheckpoint} />
                  </div>

              </div>

              <div
                className={`grid grid-cols-5 gap-3 max-h-44 overflow-y-auto mb-5 ${
                  isExpired ? "opacity-40 pointer-events-none" : ""
                }`}
              >
                {teamMembers
                  .filter((m) => m.email !== currentUser?.email)
                  .map((member) => {
                    const isSelected = selectedMember?.email === member.email;
                    const fullName = `${member.firstName || ""} ${member.lastName || ""}`.trim();
                    return (
                      <motion.div
                        key={member.email}
                        whileHover={{ scale: 1.08 }}
                        whileTap={{ scale: 0.96 }}
                        onClick={() => setSelectedMember(member)}
                        className={`cursor-pointer flex flex-col items-center justify-center p-2 rounded-xl border
                          transition-all duration-150 ${
                            isSelected
                              ? "border-blue-500 bg-blue-50"
                              : "border-gray-200 hover:bg-gray-50"
                          }`}
                      >
                        <Avatar name={fullName} src={member.profileImage} />
                        <span className="text-[11px] text-gray-700 mt-1 font-medium text-center truncate w-14">
                          {member.firstName || member.email.split("@")[0]}
                        </span>
                      </motion.div>
                    );
                  })}
              </div>

              {selectedMember && (
                <div className="flex items-center justify-between bg-gray-50 p-3 rounded-xl mb-5">
                  <div className="flex items-center gap-3">
                    <Avatar
                      name={`${selectedMember.firstName || ""} ${selectedMember.lastName || ""}`}
                      src={selectedMember.profileImage}
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-800">
                        {getDisplayName(selectedMember)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {selectedMember.role || "Member"}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedMember(null)}
                    className="text-xs text-gray-400 hover:text-red-500 transition"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>

            {/* <div className="flex items-center justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-xl bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition"
              >
                Close
              </button>
              <button
                onClick={handleRequestCollab}
                disabled={!selectedMember || isExpired}
                className="px-5 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 
                          text-white text-sm font-medium hover:from-blue-700 hover:to-indigo-700 
                          transition disabled:opacity-40"
              >
                Request Collaboration
              </button>
            </div> */}
          </div>
        </motion.div>
      </motion.div>

      {/* === FLOATING SUBMISSION BUTTON (BOTTOM RIGHT, LEFT OF PICK-UP) === */}
        <SubmissionButton
          onClick={() => {
            const isAdmin = currentUser?.role === "admin" || currentUser?.isAdmin;

            const isAssigned =
              Array.isArray(checkpoint.assignedTo)
                ? checkpoint.assignedTo.some((m) => m.email === currentUser?.email)
                : checkpoint.assignedTo?.email === currentUser?.email;

            const isPickedUp =
              Array.isArray(checkpoint.pickedUpBy)
                ? checkpoint.pickedUpBy.some((m) => m.email === currentUser?.email)
                : checkpoint.pickedUpBy?.email === currentUser?.email;

            if (!isAdmin && !isAssigned && !isPickedUp) {
              alert("🚫 You must be assigned or have picked up this checkpoint to submit.");
              return;
            }

            // only open submission if allowed
            setSubmissionOpen(true);
          }}
          hasSubmitted={hasSubmitted}
          onMouseEnter={() => setHoverSubmission(true)}
          onMouseLeave={() => setHoverSubmission(false)}
        />

        <SubmissionModal
          isOpen={isSubmissionOpen}
          onClose={() => setSubmissionOpen(false)}
          onSubmit={handleSubmitDescription}
        />

        <TransitionModal
          isOpen={isTransitionOpen}
          onClose={() => setTransitionOpen(false)}
          onContinue={handleContinueToFeedback}
        />

        <FeedbackModal
          isOpen={isFeedbackOpen}
          onClose={() => setFeedbackOpen(false)}
          onSubmit={handleSubmitFeedback}
        />


        {/* === SUBMISSION TOOLTIP === */}
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          whileHover={{ opacity: 1, y: 0 }}
          className="fixed bottom-24 right-24 px-3 py-1 bg-gray-800/90 text-white text-xs rounded-md shadow-md"
        >
          Submit Progress
        </motion.div>

      {/* === FLOATING PICK-UP BUTTON (BOTTOM RIGHT) === */}
      <motion.button
        onClick={handlePickUp}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.92, rotate: -6 }}
        animate={hasPickedUp ? { scale: [1, 1.2, 1], rotate: [0, -8, 0] } : {}}
        transition={{ type: "spring", stiffness: 200, damping: 15 }}
        title={hasPickedUp ? "You’re holding this checkpoint" : "Pick up Checkpoint"}
        className={`fixed bottom-8 right-8 z-[10000] flex items-center justify-center
                    h-14 w-14 rounded-2xl shadow-lg backdrop-blur-md border border-white/40
                    transition-all duration-200 cursor-pointer ${
                      hasPickedUp
                        ? "bg-gradient-to-br from-emerald-500 via-green-500 to-lime-400"
                        : "bg-gradient-to-br from-emerald-400 via-emerald-500 to-teal-500 hover:shadow-emerald-400/40"
                    }`}
      >
        <motion.svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-7 w-7"
          animate={
            hasPickedUp
              ? { rotate: -15, y: 1, x: -1, pathLength: [0.9, 1, 0.95] }
              : { rotate: 0, y: 0, x: 0, pathLength: 1 }
          }
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
          {/* hand icon */}
          <path d="M7 11v2a5 5 0 0 0 10 0v-1.5" />
          <path d="M12 4v5" />
          <path d="M9 6v3" />
          <path d="M15 5v4" />
        </motion.svg>

        {/* ✨ subtle pulse aura when holding */}
        {hasPickedUp && (
          <motion.span
            layoutId="aura"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0.4, 0.7, 0.4], scale: [1, 1.15, 1] }}
            transition={{
              repeat: Infinity,
              duration: 2.5,
              ease: "easeInOut",
            }}
            className="absolute inset-0 rounded-2xl bg-emerald-400/40 blur-md"
          />
        )}
      </motion.button>

      {/* === TOOLTIP === */}
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        whileHover={{ opacity: 1, y: 0 }}
        className="fixed bottom-24 right-6 px-3 py-1 bg-gray-800/90 text-white text-xs rounded-md shadow-md"
      >
        Pick up Checkpoint
      </motion.div>

    </AnimatePresence>
  );
};

export default CheckpointModalTeam;
